import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { withRouter } from 'react-router'

import { useSessionToken } from '@avantstay/auth-token'
import { Alert } from '@avantstay/backoffice-core'

import { datadogInitializer } from '@/datadogInitializer'
import { selectBackofficeRouterForUser } from '@arriere/backofficeRouter'
import { useArriereHeaders } from '@arriere/index'
import { useAuth, useClerk, useUser } from '@clerk/react'
import { PropsType } from '@contexts/Auth/Auth.types'
import AuthContext, { UserProfile } from '@contexts/Auth/AuthContext'
import { startNavigation } from '@contexts/Auth/methods/authMethods'
import { identifyCurrentUser } from '@contexts/Auth/methods/identifyCurrentUser'
import { clearPostLoginRedirect } from '@contexts/Auth/methods/postLoginRedirect'
import { ROUTES } from '@routes'
import { trackError, trackEvent } from '@tracking/customEventReport'
import { IS_PRODUCTION } from '@utils/constants'
import { setUserEmail } from '@utils/fetchApi'

import { AuthStore } from './AuthStore'

const AuthProvider: FC<PropsType> = props => {
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser()
  const { isLoaded: isAuthLoaded } = useAuth()
  const { openSignIn, signOut: clerkSignOut } = useClerk()

  const emailRef = useRef<string | null>(null)
  const getEmail = useCallback(() => emailRef.current, [])
  // Every request asks Clerk for the session token (requireSessionToken); nothing here holds one.
  useArriereHeaders({ getEmail })
  useSessionToken({
    onTokenError: useCallback((error: unknown) => {
      trackError('[Auth] Session token request failed', { error })
    }, []),
  })

  const initializeAuth = useCallback((email: string) => {
    emailRef.current = email
    setUserEmail(email)
    selectBackofficeRouterForUser(email)
  }, [])

  const cleanToken = useCallback(() => {
    setUserEmail('')
    emailRef.current = null
    selectBackofficeRouterForUser(null)
  }, [])

  const [user, setUser] = useState<UserProfile | null>(null)
  const [isAuthorizing, setIsAuthorizing] = useState(true)
  const [isMounted, setIsMounted] = useState(false)

  const isSignedIn = !!user

  const getUserPermission = useCallback(async (userProfile: UserProfile | null) => {
    const userInfo = await AuthStore.__ASYNC_userPermissions
    const userWithPermissions = {
      ...userProfile,
      voyageId: userInfo?.voyageId,
      permissions: userInfo?.userPermissions,
      roles: userInfo?.roles,
    } as UserProfile
    setUser(userWithPermissions)
    return userWithPermissions
  }, [])

  const setupUser = async (userInfo: UserProfile) => {
    initializeAuth(userInfo.email)

    identifyCurrentUser(userInfo)

    try {
      const userWithPermissions = await getUserPermission(userInfo)

      if (IS_PRODUCTION) {
        datadogInitializer({
          id: userWithPermissions?.id,
          email: userWithPermissions?.email,
          name: userWithPermissions?.name,
        })
      }

      setIsAuthorizing(false)

      if (window.location.pathname === ROUTES.login.path) {
        startNavigation(props)
      }

      clearPostLoginRedirect()
    } catch (err) {
      Alert.Error('Failed to fetch user permissions', { timeout: 10000000 })
    }
  }

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (isAuthLoaded && isUserLoaded) {
      if (clerkUser) {
        const userInfo: UserProfile = {
          id: clerkUser.id,
          name: clerkUser.fullName || '',
          email: clerkUser.primaryEmailAddress?.emailAddress || '',
          photo: clerkUser.imageUrl,
        }

        setupUser(userInfo)
      } else {
        setUser(null)
        setIsAuthorizing(false)
        cleanToken()
        identifyCurrentUser(false)
      }
    }
  }, [isAuthLoaded, isUserLoaded, clerkUser])

  const signIn = useCallback(async () => {
    trackEvent('Auth: Attempting to sign in', {})
    openSignIn()
  }, [openSignIn])

  const signOut = useCallback(async () => {
    cleanToken()
    identifyCurrentUser(false)
    await clerkSignOut({
      redirectUrl: ROUTES.login.path,
    })
  }, [clerkSignOut])

  const refreshPermissions = useCallback(async () => {
    await AuthStore.refreshUserPermission()
    await getUserPermission(user)
  }, [getUserPermission, user])

  const contextValue = useMemo(
    () => ({
      user,
      isAuthorizing,
      isSignedIn,
      signIn,
      signOut,
      refreshPermissions,
    }),
    [user, isAuthorizing, isSignedIn, signIn, signOut],
  )

  if (!isMounted) {
    return null
  }

  return <AuthContext.Provider value={contextValue}>{props.children}</AuthContext.Provider>
}

export default withRouter(AuthProvider)
